'use client'

import { ImageCropper } from '@/components/image-cropper'
import { AspectRatioValues } from '@/components/image-cropper/types'
import { useCallback, useState } from 'react'
import { ImageUploader } from './ImageUploader'

interface CroppableImageUploaderProps {
  userId: string
  currentImageUrl: string | undefined
  onImageUpdated: (url: string) => void
  canEdit?: boolean
  imageAlt: string
  onUpload: (file: File, onProgress?: (progress: number) => void) => Promise<string>
  onDelete?: () => Promise<void>
  className?: string
  width?: number
  height?: number
  successMessage?: string
  deleteSuccessMessage?: string
  errorMessage?: string
  deleteErrorMessage?: string
  aspectRatio?: AspectRatioValues
}

export function CroppableImageUploader({
  userId,
  currentImageUrl,
  onImageUpdated,
  canEdit = false,
  imageAlt,
  onUpload,
  onDelete,
  className,
  width,
  height,
  successMessage,
  deleteSuccessMessage,
  errorMessage,
  deleteErrorMessage,
  aspectRatio = AspectRatioValues.ONE_TO_ONE,
}: CroppableImageUploaderProps) {
  const [showCropper, setShowCropper] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null)
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('')

  const handleImageSelected = useCallback((file: File) => {
    const imageUrl = URL.createObjectURL(file)
    setSelectedImageFile(file)
    setSelectedImageUrl(imageUrl)
    setShowCropper(true)
  }, [])

  const handleCropCancel = useCallback(() => {
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl)
    }
    setSelectedImageFile(null)
    setSelectedImageUrl('')
    setShowCropper(false)
  }, [selectedImageUrl])

  const handleCropApply = useCallback(
    async (croppedImageBlob: Blob) => {
      try {
        // Convert Blob to File
        const fileName = selectedImageFile?.name || `cropped-${Date.now()}.jpg`
        const croppedFile = new File([croppedImageBlob], fileName, {
          type: croppedImageBlob.type,
        })

        // Upload the file
        const uploadedUrl = await onUpload(croppedFile)

        // Notify parent component
        onImageUpdated(uploadedUrl)

        // Cleanup
        if (selectedImageUrl) {
          URL.revokeObjectURL(selectedImageUrl)
        }
        setSelectedImageFile(null)
        setSelectedImageUrl('')
        setShowCropper(false)
      } catch (error) {
        console.error('Error processing cropped image:', error)
      }
    },
    [onUpload, onImageUpdated, selectedImageFile, selectedImageUrl],
  )

  // Custom uploader function for ImageUploader that intercepts the file for cropping
  const handleFileUpload = useCallback(
    async (file: File) => {
      handleImageSelected(file)
      // Return empty string as the actual upload happens after cropping
      return ''
    },
    [handleImageSelected],
  )

  return (
    <>
      <ImageUploader
        currentImageUrl={currentImageUrl}
        onImageUpdated={onImageUpdated}
        onUpload={handleFileUpload}
        onDelete={onDelete}
        canEdit={canEdit}
        imageAlt={imageAlt}
        width={width}
        height={height}
        className={className}
        successMessage={successMessage}
        deleteSuccessMessage={deleteSuccessMessage}
        errorMessage={errorMessage}
        deleteErrorMessage={deleteErrorMessage}
      />

      {showCropper && selectedImageUrl && (
        <ImageCropper
          imageSrc={selectedImageUrl}
          onCancel={handleCropCancel}
          onApply={handleCropApply}
          defaultAspectRatio={aspectRatio}
        />
      )}
    </>
  )
}
